<template>
  <div>
    <p class="text-muted small mb-3">
      {{ $t("Frame the production and take a photo directly from this computer.") }}
    </p>

    <div v-if="desktopCameraError" class="alert alert-danger small">
      {{ desktopCameraError }}
    </div>

    <button
      v-if="desktopCameraError"
      class="btn btn-outline-violet text-uppercase w-100 mb-3"
      :disabled="isDesktopUploading || disabled"
      @click="initDesktopCamera"
    >
      {{ $t("Retry camera") }}
    </button>

    <div v-if="desktopCameraPhase === 'captured'" class="alert alert-success small">
      {{ $t("Photo ready to be sent.") }}
    </div>

    <img
      v-if="desktopPhotoPreviewUrl"
      class="w-100 rounded bg-dark mb-3"
      :src="desktopPhotoPreviewUrl"
      :alt="$t('CAM photo preview')"
      style="max-height: 260px; object-fit: contain"
    />
    <video
      v-else
      ref="desktopCameraVideoEl"
      class="w-100 rounded bg-dark mb-3"
      playsinline
      muted
      style="max-height: 260px; object-fit: cover"
    />

    <div v-if="desktopCameraPhase === 'uploading'" class="mb-3">
      <div class="progress">
        <div
          class="progress-bar bg-violet"
          role="progressbar"
          :style="{ width: desktopUploadPercent + '%' }"
          :aria-valuenow="desktopUploadPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {{ desktopUploadPercent }}%
        </div>
      </div>
    </div>

    <div class="d-flex flex-wrap gap-2 justify-content-center">
      <template v-if="desktopCameraPhase === 'idle' || desktopCameraPhase === 'preview'">
        <button
          class="btn btn-violet text-uppercase"
          :disabled="isDesktopUploading || disabled"
          @click="handleDesktopPrimaryAction"
        >
          {{ $t("Take photo") }}
        </button>
      </template>
      <template v-if="desktopCameraPhase === 'captured'">
        <button
          class="btn btn-outline-violet text-uppercase"
          :disabled="disabled"
          @click="retakeDesktopPhoto"
        >
          {{ $t("Retake photo") }}
        </button>
        <button
          class="btn btn-violet text-uppercase"
          :disabled="isDesktopUploading || disabled"
          @click="sendDesktopPhoto"
        >
          {{ isDesktopUploading ? $t("Sending...") : $t("Confirm photo from computer") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { assignPhotoUploadUrl, completePhotoUpload } from "@/use/repositories/camProduction";
import { firstErrorMessage } from "@/use/utilities/camProduction/firstErrorMessage";
import { formatFilename } from "@/use/utilities/media/formatFilename";

export default {
  name: "CamProductionDesktopUploader",
  props: {
    photoUploadToken: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["request-token", "uploaded", "error"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const uploadBlobToS3 = (actionUrl, inputs, blob, filename, onProgress) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();

      Object.keys(inputs).forEach((key) => formData.append(key, inputs[key]));
      formData.append("file", blob, filename);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && typeof onProgress === "function") {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 204 || xhr.status === 200) {
          resolve();
          return;
        }

        reject(new Error(`Upload failed (${xhr.status})`));
      });

      xhr.addEventListener("error", () => reject(new Error("Network error")));

      xhr.open("POST", actionUrl);
      xhr.send(formData);
    });
    const desktopCameraVideoEl = ref(null);
    const desktopCameraStreamRef = ref(null);
    const desktopCameraReady = ref(false);
    const desktopCameraPhase = ref("idle");
    const desktopCameraError = ref("");
    const desktopPhotoBlob = ref(null);
    const desktopPhotoPreviewUrl = ref("");
    const desktopUploadPercent = ref(0);
    const isDesktopUploading = ref(false);

    const stopDesktopCameraStream = () => {
      const stream = desktopCameraStreamRef.value;

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        desktopCameraStreamRef.value = null;
      }

      desktopCameraReady.value = false;
    };

    const revokeDesktopPhotoPreview = () => {
      if (desktopPhotoPreviewUrl.value) {
        URL.revokeObjectURL(desktopPhotoPreviewUrl.value);
        desktopPhotoPreviewUrl.value = "";
      }
    };

    const resetDesktopPhotoCapture = () => {
      revokeDesktopPhotoPreview();
      desktopPhotoBlob.value = null;
      desktopUploadPercent.value = 0;
      desktopCameraError.value = "";
    };

    const attachDesktopCameraToVideo = () => {
      const el = desktopCameraVideoEl.value;
      const stream = desktopCameraStreamRef.value;

      if (el && stream && !desktopPhotoPreviewUrl.value) {
        el.srcObject = stream;
        el.play().catch(() => {});
      }
    };

    const initDesktopCamera = async () => {
      desktopCameraError.value = "";

      if (!navigator.mediaDevices?.getUserMedia) {
        desktopCameraError.value = i18n.t("This browser does not support the camera.");
        return;
      }

      try {
        stopDesktopCameraStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });

        desktopCameraStreamRef.value = stream;
        desktopCameraReady.value = true;
        desktopCameraPhase.value = "preview";
        await nextTick();
        attachDesktopCameraToVideo();
      } catch (error) {
        console.error(error);
        desktopCameraError.value = i18n.t("Unable to access this computer's camera.");
      }
    };

    const captureDesktopPhoto = () => {
      desktopCameraError.value = "";

      if (!desktopCameraVideoEl.value || !props.photoUploadToken) {
        desktopCameraError.value = i18n.t("Camera or photo link not available.");
        return;
      }

      const video = desktopCameraVideoEl.value;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;

      const context = canvas.getContext("2d");

      if (!context) {
        desktopCameraError.value = i18n.t("Unable to capture the photo from this device.");
        return;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (!blob) {
          desktopCameraError.value = i18n.t("Unable to generate the photo.");
          return;
        }

        revokeDesktopPhotoPreview();
        desktopPhotoBlob.value = blob;
        desktopPhotoPreviewUrl.value = URL.createObjectURL(blob);
        desktopCameraPhase.value = "captured";
      }, "image/jpeg", 0.92);
    };

    const handleDesktopPrimaryAction = async () => {
      if (!props.photoUploadToken) {
        emit("request-token");
        return;
      }

      if (!desktopCameraReady.value) {
        await initDesktopCamera();
        return;
      }

      captureDesktopPhoto();
    };

    const retakeDesktopPhoto = async () => {
      resetDesktopPhotoCapture();
      desktopCameraPhase.value = "preview";
      await nextTick();
      attachDesktopCameraToVideo();
    };

    const sendDesktopPhoto = async () => {
      desktopCameraError.value = "";

      if (!desktopPhotoBlob.value || !props.photoUploadToken) {
        desktopCameraError.value = i18n.t("Photo or photo link not available.");
        return;
      }

      isDesktopUploading.value = true;
      desktopCameraPhase.value = "uploading";
      desktopUploadPercent.value = 0;

      const namedFile = new File([desktopPhotoBlob.value], "cam-photo.jpg", {
        type: desktopPhotoBlob.value.type || "image/jpeg",
      });
      const filename = formatFilename(namedFile);

      try {
        const { data } = await assignPhotoUploadUrl(
          props.photoUploadToken,
          { filename },
          false,
        );

        const inputs = data.inputs;
        const actionUrl = data.attributes?.action;

        if (!inputs || !actionUrl) {
          throw new Error(i18n.t("Invalid signed URL response."));
        }

        await uploadBlobToS3(actionUrl, inputs, desktopPhotoBlob.value, filename, (percent) => {
          desktopUploadPercent.value = percent;
        });

        const { log } = await completePhotoUpload(
          props.photoUploadToken,
          { filename },
          false,
        );

        emit("uploaded", log ?? null);
        resetDesktopPhotoCapture();
        desktopCameraPhase.value = "idle";
      } catch (error) {
        const message = firstErrorMessage(error, i18n.t("An error occurred while saving the CAM photo."));
        desktopCameraError.value = message;
        desktopCameraPhase.value = "captured";
        emit("error", message);
      } finally {
        isDesktopUploading.value = false;
      }
    };

    watch(() => props.photoUploadToken, async (token) => {
      if (!token || desktopCameraPhase.value !== "idle") {
        return;
      }

      await initDesktopCamera();
    });

    watch(() => desktopCameraVideoEl.value, () => {
      attachDesktopCameraToVideo();
    });

    onBeforeUnmount(() => {
      stopDesktopCameraStream();
      revokeDesktopPhotoPreview();
    });

    return {
      desktopCameraVideoEl,
      desktopCameraPhase,
      desktopCameraError,
      desktopPhotoPreviewUrl,
      desktopUploadPercent,
      isDesktopUploading,
      initDesktopCamera,
      handleDesktopPrimaryAction,
      retakeDesktopPhoto,
      sendDesktopPhoto,
    };
  },
};
</script>
